/* global Promise */
import {useMutation, useQuery} from '@apollo/client';
import {
  Checkbox,
  Form,
  Input,
  InputNumber,
  Segmented,
  Select,
  Spin,
  Switch,
} from 'antd';
import classnames from 'classnames';
import {useState} from 'react';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {makeSelectSubTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import {
  GET_FEEDBACK_DIGEST_SETTINGS,
  SEND_TEST_FEEDBACK_DIGEST,
  UPDATE_FEEDBACK_DIGEST_SETTINGS,
} from '../resolvers';
import styles from './feedbackDigest.module.scss';
import {
  DAYS_OF_WEEK,
  FREQUENCIES,
  SECTIONS,
  describeLastRun,
  describeSchedule,
  formatDayOfWeek,
  formatHour,
  hasNoDestination,
  isSameSettings,
  toFormValues,
  toInput,
  validateEmailRecipients,
  validateSections,
  validateSlackChannelId,
} from './feedbackDigestHelpers';

const asRule = validate => ({
  validator: (_, value) => {
    const error = validate(value);
    return error ? Promise.reject(new Error(error)) : Promise.resolve();
  },
});

const RUN_TONE = {SENT: 'good', SENDING: 'info', FAILED: 'bad'};

const Card = ({title, hint, children, className}) => (
  <section className={classnames(styles.card, className)}>
    <header className={styles.cardHeader}>
      <h3>{title}</h3>
      {hint && <p>{hint}</p>}
    </header>
    {children}
  </section>
);

export const FeedbackDigestPage = () => {
  const [form] = Form.useForm();
  const [saved, setSaved] = useState(null);
  const [values, setValues] = useState(null);
  const [lastRun, setLastRun] = useState(null);

  const operations = useSelector(state =>
    makeSelectSubTabOperations(state, NavTabsEnum.Analytics, 'feedback-digest'),
  );
  const canUpdate = !!operations?.update;

  const applySettings = settings => {
    const formValues = toFormValues(settings);
    form.setFieldsValue(formValues);
    setSaved(formValues);
    setValues(formValues);
    setLastRun(settings.lastRun || null);
  };

  const {loading, data} = useQuery(GET_FEEDBACK_DIGEST_SETTINGS, {
    fetchPolicy: 'no-cache',
    onCompleted: res => {
      const node = res?.getFeedbackDigestSettings;
      if (node?.__typename === 'Error') toast.error(node.message);
      else if (node?.data) applySettings(node.data);
    },
    onError: error => toast.error(error.message),
  });
  const loadError =
    data?.getFeedbackDigestSettings?.__typename === 'Error'
      ? data.getFeedbackDigestSettings.message
      : null;

  const [updateSettings, {loading: saving}] = useMutation(
    UPDATE_FEEDBACK_DIGEST_SETTINGS,
  );
  const [sendTest, {loading: sendingTest}] = useMutation(
    SEND_TEST_FEEDBACK_DIGEST,
  );

  const handleSave = async formValues => {
    try {
      const {data: res} = await updateSettings({
        variables: {input: toInput(formValues)},
      });
      const node = res?.updateFeedbackDigestSettings;
      if (node?.__typename === 'Error') return toast.error(node.message);
      applySettings(node.data);
      toast.success(node.message || 'Feedback digest settings saved');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSendTest = async () => {
    try {
      const {data: res} = await sendTest();
      const node = res?.sendTestFeedbackDigest;
      if (node?.__typename === 'Error') return toast.error(node.message);
      toast.success(`Test digest on its way to ${node.data?.recipient}`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className={styles.centered}>
        <Spin size="large" />
      </div>
    );
  }

  if (!saved) {
    return (
      <div className={classnames(styles.banner, styles.bannerBad)}>
        {loadError || 'Could not load the feedback digest settings.'}
      </div>
    );
  }

  const current = values || saved;
  const isDirty = !isSameSettings(current, saved);
  const frequency = current.frequency;
  const noDestination = hasNoDestination(current);
  const destinations = [
    current.slackChannelId?.trim() && 'Slack',
    current.emailRecipients?.length &&
      `${current.emailRecipients.length} email${
        current.emailRecipients.length === 1 ? '' : 's'
      }`,
  ].filter(Boolean);
  const status = !current.enabled
    ? {label: 'Off', tone: 'muted'}
    : noDestination
    ? {label: 'Not sending', tone: 'warn'}
    : {label: 'On', tone: 'good'};

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div>
          <span className={styles.eyebrow}>Analytics</span>
          <h2 className={styles.title}>Feedback digest</h2>
          <p className={styles.lead}>
            A summary of checkout abandonment, NPS, in-app reviews and mini
            feedback, posted to Slack and emailed to staff. Changes apply to the
            next run.
          </p>
          <div className={styles.summary}>
            <span className={classnames(styles.pill, styles[status.tone])}>
              {status.label}
            </span>
            <span>{describeSchedule(current)}</span>
            <span className={styles.sep}>·</span>
            <span>
              {destinations.length
                ? `To ${destinations.join(' + ')}`
                : 'No destination yet'}
            </span>
          </div>
        </div>
      </div>

      {noDestination && (
        <div className={classnames(styles.banner, styles.bannerWarn)}>
          Add a Slack channel or an email recipient to start the digest.
        </div>
      )}

      <div
        className={classnames(
          styles.lastRun,
          styles[RUN_TONE[lastRun?.status] || 'muted'],
        )}>
        <span className={styles.dot} />
        <div>
          <strong>{describeLastRun(lastRun)}</strong>
          {lastRun?.status === 'FAILED' && (
            <p>
              Retried automatically up to 3 times within a day of the send time.
              After that, check the Slack channel id and that the Daba bot is in
              the channel.
            </p>
          )}
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        disabled={!canUpdate}
        initialValues={saved}
        onValuesChange={(_, all) => setValues(all)}
        onFinish={handleSave}
        className={styles.form}>
        <div className={styles.grid}>
          <Card
            title="Schedule"
            hint={FREQUENCIES.find(f => f.value === frequency)?.hint}>
            <div className={styles.switchRow}>
              <div>
                <strong>Send the digest</strong>
                <p>The test email works either way.</p>
              </div>
              <Form.Item name="enabled" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
            </div>

            <Form.Item
              name="frequency"
              label="Frequency"
              tooltip="How often the digest goes out. Each digest covers the last full period, in GMT.">
              <Segmented
                block
                options={FREQUENCIES.map(({value, label}) => ({
                  value,
                  label,
                }))}
              />
            </Form.Item>

            <div className={styles.row}>
              {/* Both days stay in the form so switching back restores them. */}
              <Form.Item
                name="dayOfWeek"
                label="Send day"
                hidden={frequency !== 'WEEKLY'}
                tooltip="The weekly digest still reports the week that ended the previous Sunday.">
                <Select
                  options={DAYS_OF_WEEK.map(day => ({
                    value: day,
                    label: formatDayOfWeek(day),
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="dayOfMonth"
                label="Day of the month"
                hidden={frequency !== 'MONTHLY'}
                tooltip="1–28, so every month has it. The monthly digest reports the whole previous month."
                rules={[
                  {
                    type: 'integer',
                    min: 1,
                    max: 28,
                    message: 'dayOfMonth must be from 1 to 28',
                  },
                ]}>
                <InputNumber min={1} max={28} precision={0} />
              </Form.Item>
              <Form.Item
                name="hourGmt"
                label="Send hour"
                tooltip="In GMT. If the servers are down then, the digest still goes out within 24 hours.">
                <Select
                  options={Array.from({length: 24}, (_, hour) => ({
                    value: hour,
                    label: formatHour(hour),
                  }))}
                />
              </Form.Item>
            </div>
          </Card>

          <Card title="Where it goes" hint="Slack, email, or both.">
            <Form.Item
              name="slackChannelId"
              label="Slack channel id"
              tooltip="The channel the digest is posted to. Leave empty to post nothing to Slack."
              extra="In Slack, open the channel → channel name → copy the Channel ID at the bottom. Invite the Daba bot to the channel, or the post will fail."
              rules={[asRule(validateSlackChannelId)]}>
              <Input placeholder="C0123456789" />
            </Form.Item>

            <Form.Item
              name="emailRecipients"
              label="Email recipients"
              tooltip="Staff addresses (@joindaba.com or @dabafinance.com) that get the digest by email. At most 20."
              extra="Press Enter or comma after each address."
              rules={[asRule(validateEmailRecipients)]}>
              <Select
                mode="tags"
                open={false}
                tokenSeparators={[',', ' ']}
                placeholder="name@joindaba.com"
              />
            </Form.Item>
          </Card>

          <Card
            title="What's in it"
            hint="A section with no data shows “No responses”."
            className={styles.wide}>
            <Form.Item
              name="sections"
              tooltip="What the digest contains."
              rules={[asRule(validateSections)]}>
              <Checkbox.Group className={styles.sections}>
                {SECTIONS.map(section => (
                  <Checkbox
                    key={section.value}
                    value={section.value}
                    className={classnames(
                      styles.section,
                      current.sections?.includes(section.value) &&
                        styles.sectionOn,
                    )}>
                    <span className={styles.sectionLabel}>
                      {section.label}
                      {section.comingSoon && (
                        <span className={styles.soon}>Coming soon</span>
                      )}
                    </span>
                    <span className={styles.sectionHint}>
                      {section.description}
                    </span>
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>

            <Form.Item
              name="commentsPerSection"
              label="Comments per section"
              tooltip="Recent free-text comments under each section (0 hides them). Cut to 200 characters, with no names, emails or user ids."
              rules={[
                {
                  type: 'integer',
                  min: 0,
                  max: 20,
                  message: 'commentsPerSection must be from 0 to 20',
                },
              ]}>
              <InputNumber min={0} max={20} precision={0} />
            </Form.Item>
          </Card>
        </div>
      </Form>

      {canUpdate && (
        <div className={styles.actions}>
          <span className={styles.actionsHint}>
            {isDirty
              ? 'Unsaved changes. Save to send a test.'
              : 'All changes saved.'}
          </span>
          <button
            type="button"
            className={styles.secondaryBtn}
            disabled={sendingTest || isDirty}
            onClick={handleSendTest}>
            {sendingTest ? 'Requesting…' : 'Send me a test'}
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            disabled={saving || !isDirty}
            onClick={() => form.submit()}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
    </div>
  );
};
